import * as yup from "yup";
import { useTranslation } from "@shared/utils/internationalization";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import useCsrfTokens from "@shared/modules/auth/hooks/useCsrfTokens";
import axios from "axios";
import { useCallback, useState } from "react";
import FormControl from "@ui-kit/form/FormControl/FormControl.web";
import FormLabel from "@ui-kit/form/FormLabel/FormLabel.web";

import Divider from "@ui-kit/data-display/Divider/Divider.web";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAndroidTWACheck } from "@shared/hooks/platform/useAndroidTWACheck";
import { isPlatform } from "@shared/utils/get-platform";
import Input, {
  InputGroup,
  InputRightElement,
} from "@ui-kit/form/Input/Input.web";
import Box from "@ui-kit/layout/Box/Box.web";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import FormErrorMessage from "@ui-kit/form/FormErrorMessage/FormErrorMessage.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Button from "@ui-kit/form/Button/Button.web";
import Track from "@shared/modules/analytics/components/Track/Track.web";
import Icon from "@ui-kit/icons/Icon.web";
import { logAppInfo } from "@shared/utils/errors/errorLogger";
import { APP_ENV } from "@shared/env";
import dynamic from "next/dynamic";
import ErrorBoundary from "@shared/components/application/other/ErrorBoundary/ErrorBoundary.web";
import CaptchaBadgeStyler from "@ui-application/other/CaptchaBadgeStyler/CaptchaBadgeStyler";
import FacebookLoginButton from "../FacebookLoginButton/FacebookLoginButton.web";
import type { LoginFormSharedProps } from "./LoginForm.props";

// TODO revert to normal import when we dont need the enable_google_sso waffle switch anymore
const GoogleLoginButton = dynamic(
  () => import("../GoogleLoginButton/GoogleLoginButton.web"),
  { ssr: false }
);

const SamlLoginButton = dynamic(
  () => import("../SamlLoginButton/SamlLoginButton.web"),
  { ssr: false }
);

export type LoginFormProps = LoginFormSharedProps & {
  // Add any web specific props here.
};

type LoginFormData = {
  email: string;
  password: string;
};

export default function LoginForm(props: LoginFormProps): JSX.Element {
  const { onSuccess, onOtpRequired, onForgotPasswordClick, onSsoClick } = props;
  const { login } = useUserSessionAuthState();
  const { refetch } = useCsrfTokens({ enabled: false });
  const { t } = useTranslation("auth");
  const [showPassword, setShowPassword] = useState(false);
  // Facebook login is unsupported on Android WebViews.
  const isAndroidCompatible = useAndroidTWACheck();

  const onSubmit = useCallback(
    async (credentials: { email: string; password: string }) => {
      try {
        const { email, password } = credentials;
        const response = await login(email, password);

        if (APP_ENV === "preview") {
          await refetch();
        }
        if (response && "requires_otp" in response && response.requires_otp) {
          onOtpRequired(credentials);
          return;
        }
        onSuccess();
      } catch (error) {
        if (axios.isAxiosError(error)) {
          logAppInfo(error.response?.data.detail);
        }
      }
    },
    [login, onSuccess, refetch, onOtpRequired]
  );

  const loginSchema = yup.object().shape({
    email: yup
      .string()
      .required(t("Email is required"))
      .email(t("Invalid email address")),
    password: yup.string().required(t("Password is required")),
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - types are correct for yupResolver
    resolver: yupResolver(loginSchema),
  });

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} data-sentry-mask>
        <CaptchaBadgeStyler />
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <FormControl isInvalid={Boolean(errors.email)}>
              <FormLabel htmlFor="email">{t("Email address")}</FormLabel>
              <Input
                {...field}
                id="email"
                placeholder={t("Email address")}
                data-testid="email-address-input"
              />
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
            </FormControl>
          )}
        />

        <Box mb="lg" />

        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <FormControl isInvalid={Boolean(errors.password)}>
              <FormLabel htmlFor="password">{t("Password")}</FormLabel>
              <InputGroup alignItems="center">
                <Input
                  {...field}
                  id="password"
                  placeholder={t("Password")}
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  data-testid="password-input"
                />
                <InputRightElement pr="3xs">
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label="show password"
                    onClick={() => setShowPassword(!showPassword)}
                    iconName={showPassword ? "Hide" : "Show"}
                    iconColor="font.dark"
                    iconOutline={false}
                    _active={{ backgroundColor: "transparent" }}
                    _hover={{ backgroundColor: "transparent" }}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
            </FormControl>
          )}
        />

        <Box h="element.2xs" />
        <Flex width="100%" alignItems="center" justifyContent="flex-end">
          <Button
            variant="link"
            color="fg.subtle"
            fontSize="sm"
            onClick={onForgotPasswordClick}
            data-testid="forgot-password-button"
          >
            <Icon.Lock.Outline boxSize="element.lg" color="fg.subtle" />
            {t("Forgot your password?")}
          </Button>
        </Flex>
        <Box h="element.3xl" />

        <Track.Click analytics={{ label: "showpass-login-button" }}>
          <Button
            width="100%"
            variant="solid"
            isLoading={isSubmitting}
            type="submit"
            data-testid="log-in-button"
          >
            {t("Log in")}
          </Button>
        </Track.Click>
      </form>

      <ErrorBoundary fallback={null}>
        {!isPlatform("desktop") &&
          (isAndroidCompatible == null || isAndroidCompatible === true) && (
            <>
              <Box h="element.2xl" />
              <Flex alignItems="center">
                <Divider width="100%" />
                <Box w="100%" textAlign="center" color="gray.600">
                  {t("Or log in with")}
                </Box>
                <Divider width="100%" />
              </Flex>
              <Box h="element.2xl" />

              <Flex gap="2xs">
                <GoogleLoginButton title={t("Google")} onSuccess={onSuccess} />
                <FacebookLoginButton
                  title={t("Facebook")}
                  onSuccess={onSuccess}
                />
                <SamlLoginButton
                  title={t("SSO")}
                  onSuccess={onSuccess}
                  onSsoClick={onSsoClick}
                />
              </Flex>
            </>
          )}
      </ErrorBoundary>
    </>
  );
}
